import Intercom from '@intercom/messenger-js-sdk';

type IntercomCall = unknown[];
type IntercomSettings = Parameters<typeof Intercom>[0];

interface IntercomQueueStub {
  (...args: IntercomCall): void;
  isPreBootQueue: true;
  calls: IntercomCall[];
  onUserIntent: () => void;
}

// Calls that mean the user wants the messenger right now (show, showNewMessage, startTour, ...).
const USER_INTENT_METHOD = /^(?:show|start)/;

const getQueueStub = (): IntercomQueueStub | null => {
  const current = window.Intercom as Partial<IntercomQueueStub> | undefined;
  return current?.isPreBootQueue ? current as IntercomQueueStub : null;
};

/**
 * Installs a lightweight `window.Intercom` until the real widget is booted, so
 * `window.Intercom('onShow', ...)`, `show()`, etc. keep working while the SDK is deferred.
 * Calls are replayed on boot; a user-intent call triggers `onUserIntent` to boot right away.
 */
export function installIntercomQueue(onUserIntent: () => void) {
  const existingStub = getQueueStub();

  if (existingStub) {
    // A remounted owner (e.g. after a market switch) must receive early support clicks;
    // keep the calls queued so far.
    existingStub.onUserIntent = onUserIntent;
    return;
  }

  if (window.Intercom) {
    return;
  }

  const stub = ((...args: IntercomCall) => {
    stub.calls.push(args);

    if (typeof args[0] === 'string' && USER_INTENT_METHOD.test(args[0])) {
      stub.onUserIntent();
    }
  }) as IntercomQueueStub;

  stub.isPreBootQueue = true;
  stub.calls = [];
  stub.onUserIntent = onUserIntent;
  window.Intercom = stub as unknown as typeof window.Intercom;
}

/** Loads the Intercom widget and replays calls made while it was deferred. */
export function bootIntercom(settings: IntercomSettings) {
  const stub = getQueueStub();

  if (stub) {
    // The SDK only injects the widget when `window.Intercom` is not a function yet.
    delete window.Intercom;
  }

  Intercom(settings);

  stub?.calls.forEach(args => (window.Intercom as (...callArgs: IntercomCall) => void)(...args));
}
